import { Injectable } from '@angular/core';
import { info } from '@4players/odin-common';
import { normalizeRoomId } from '../../shared/utils/room-id';
import { isTauri } from '../../shared/helpers/platform';
import { tenantOrigin } from '../../shared/helpers/tenant';

export interface ParsedDeepLink {
  roomId: string | null;
  userName: string | null;
  autoJoin: boolean;
  password: string | null;
  breakoutRoomId: string | null;
  recoverToken: string | null;
}

// Pure parser for the entry-page deep-link surface — exported separately from
// the service so specs can exercise arbitrary hrefs (the service itself is
// bound to `location.href`, which cannot be faked inside a Karma page).
//
// Supported inputs on the entry URL:
//   - Path:     `/<roomId>`         — sets `roomId` (decoded). Empty path = null.
//   - Query:    `?userName=<name>`  — sets `userName` (trimmed). Empty = null.
//   - Query:    `?autoJoin=1`       — sets `autoJoin = true`. Anything else = false.
//   - Query:    `?breakoutRoomId=<id>` — sets `breakoutRoomId` (trimmed, 3-100
//               chars, else null). Places the invited user into that breakout
//               room on join.
//   - Fragment: `#p=<password>`     — sets `password`. The fragment is the right
//               place because browsers do not send fragments to the server, so
//               the cipher password never touches HTTP.
//   - Fragment: `#recover=<token>`  — sets `recoverToken`: the one-time token
//               from a role-password recovery e-mail (see
//               PasswordRecoveryService). Same reasoning as `#p=`: the secret
//               never reaches the server through the URL, and it is scrubbed
//               from the address bar once read. A recovery link has no path.
//
// `file://` URLs (Electron-style entrypoints) skip parsing entirely; all
// fields become null/false.
const NO_DEEP_LINK: ParsedDeepLink = {
  roomId: null,
  userName: null,
  autoJoin: false,
  password: null,
  breakoutRoomId: null,
  recoverToken: null,
};

export function parseDeepLink(href: string): ParsedDeepLink {
  if (href.startsWith('file://')) {
    return NO_DEEP_LINK;
  }
  const url = new URL(href);
  const rawRoom = decodeURI(url.pathname.split('/')[1] ?? '');
  const hash = parseHash(url.hash);
  return {
    // Canonicalize the path segment to the same slug the server will store, so an
    // old invite link (`/Some-Room`) resolves to one room across all openers and
    // the local id matches the id the join response echoes back. Invalid → null.
    roomId: rawRoom.length > 0 ? normalizeRoomId(rawRoom) : null,
    userName: url.searchParams.get('userName')?.trim() || null,
    autoJoin: url.searchParams.get('autoJoin') === '1',
    password: hash.password,
    breakoutRoomId: parseBreakoutRoomId(url.searchParams.get('breakoutRoomId')),
    recoverToken: hash.recoverToken,
  };
}

function parseHash(hash: string): { password: string | null; recoverToken: string | null } {
  const none = { password: null, recoverToken: null };
  if (!hash) return none;
  // location.hash includes the leading '#'.
  const trimmed = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!trimmed) return none;
  // `p=<password>` / `recover=<token>` (URL-encoded). URLSearchParams so
  // further fragment keys can be added without changing the parser.
  const params = new URLSearchParams(trimmed);
  const password = params.get('p');
  const recoverToken = params.get('recover')?.trim();
  return {
    password: password && password.length > 0 ? password : null,
    recoverToken: recoverToken && recoverToken.length > 0 ? recoverToken : null,
  };
}

function parseBreakoutRoomId(raw: string | null): string | null {
  const value = raw?.trim() ?? '';
  // Mirror the create-field constraints (min 3 / max 100, see
  // `breakoutRoomNameControl` in conference-panel.component.ts). Out-of-range
  // values are ignored rather than rejected — the invited user simply lands
  // in the main room. NOT normalized via `normalizeRoomId`: breakout ids are
  // free-form strings matched verbatim against `user.data.breakoutRoomId`.
  return value.length >= 3 && value.length <= 100 ? value : null;
}

// Reads `location.href` once at construction and exposes the parsed values as
// readonly fields; never touches stores, never triggers side-effects.
// Consumers (AppService, App) decide what to do with the values.
@Injectable({ providedIn: 'root' })
export class DeepLinkService {
  readonly roomId: string | null;
  readonly userName: string | null;
  readonly autoJoin: boolean;
  readonly password: string | null;
  readonly breakoutRoomId: string | null;
  readonly recoverToken: string | null;

  constructor() {
    // The native app's page URL (`tauri://localhost/…`) is the bundled entry
    // point, never an invite.
    const parsed = isTauri ? NO_DEEP_LINK : parseDeepLink(location.href);
    this.roomId = parsed.roomId;
    this.userName = parsed.userName;
    this.autoJoin = parsed.autoJoin;
    this.password = parsed.password;
    this.breakoutRoomId = parsed.breakoutRoomId;
    this.recoverToken = parsed.recoverToken;

    if (
      this.roomId ||
      this.userName ||
      this.autoJoin ||
      this.password ||
      this.breakoutRoomId ||
      this.recoverToken
    ) {
      info(
        `deep-link parsed; [roomId: ${this.roomId ?? '(none)'}, userName: ${this.userName ?? '(none)'}, autoJoin: ${this.autoJoin}, password: ${this.password ? '(present)' : '(none)'}, breakoutRoomId: ${this.breakoutRoomId ?? '(none)'}, recoverToken: ${this.recoverToken ? '(present)' : '(none)'}]`
      );
    }
  }

  buildInviteLink(
    roomId: string,
    password?: string | null,
    breakoutRoomId?: string | null
  ): string {
    let link = `${tenantOrigin()}/${roomId}`;
    if (breakoutRoomId) link += `?breakoutRoomId=${encodeURIComponent(breakoutRoomId)}`;
    if (password) link += `#p=${encodeURIComponent(password)}`;
    return link;
  }
}
